import React, { Suspense, useEffect } from 'react';

import { useModal } from 'context/ModalContext';
import { useDeclineWelcomeCoupon, useWelcomeCoupon } from 'entities/coupon';
import { isPassportName } from 'features/auth/lib';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useGetMemberDetail } from 'shared/api';
import { lazyWithRetry } from 'shared/lib/lazyRetry';
import { toast } from 'sonner';
import { useInfoStore } from 'store/useInfoStore';

// 모달은 열릴 때만 렌더되므로 코드도 열릴 때 내려받는다 — 로그인 트리가 엔트리 번들에서 빠짐
const FindEmailModal = lazyWithRetry(() => import('pages/login/FindEmailModal'));
const LoginModal = lazyWithRetry(() => import('pages/login/LoginModal'));
const OnboardingModal = lazyWithRetry(() => import('pages/login/OnboardingModal'));
const PasswordModal = lazyWithRetry(() => import('pages/login/PasswordModal'));
const ResetModal = lazyWithRetry(() => import('pages/login/ResetModal'));
const SignupModal = lazyWithRetry(() => import('pages/login/SignupModal'));

const Modals = () => {
  const { modalType, openModal, closeModal, closeModalWithRedirectClear, MODAL_TYPES } = useModal();

  const { eligible } = useWelcomeCoupon();
  const { mutateAsync: decline, isPending } = useDeclineWelcomeCoupon();
  const { isLogin, firstName, lastName, profileCompleted } = useInfoStore();
  const { t } = useTranslation('auth');
  // 온보딩을 이름 없이 떠난(창 닫기·새로고침) 소셜 가입 회원은 접속할 때마다 이름 입력창을 다시 띄운다.
  // 이 기기 저장값이 비었어도 다른 기기·신청서로 서버엔 이미 이름이 있을 수 있어, 서버 값을 받아 본 뒤에 정한다
  const isNamedOnDevice = isPassportName(firstName) && isPassportName(lastName);
  const { data: memberDetail } = useGetMemberDetail(isLogin && !profileCompleted && !isNamedOnDevice);
  const isNamedOnServer =
    isPassportName(memberDetail?.memberDetailInfo.firstName) && isPassportName(memberDetail?.memberDetailInfo.lastName);
  const needsName = isLogin && !profileCompleted && !isNamedOnDevice && memberDetail !== undefined && !isNamedOnServer;
  // 새로고침은 최초 가입 혜택을 소멸시키지 않는다. 재로그인은 서버에서 대상 종료.
  useEffect(() => {
    if ((eligible || needsName) && !profileCompleted && modalType === MODAL_TYPES.NONE) {
      openModal(MODAL_TYPES.ONBOARDING);
    }
  }, [eligible, needsName, profileCompleted, modalType, openModal, MODAL_TYPES.NONE, MODAL_TYPES.ONBOARDING]);

  const handleOnboardingClose = async () => {
    if (isPending) return;
    try {
      if (!profileCompleted) await decline();
      closeModalWithRedirectClear();
    } catch {
      toast.error(t('onboarding.saveError'));
    }
  };

  if (modalType === MODAL_TYPES.NONE) return null;

  // 로그인 모달은 redirect 까지 같이 정리해야 해서 closeModalWithRedirectClear 사용
  const handleBackdropClose = () => {
    // 온보딩은 이름이 필수라 바깥을 눌러도 닫지 않는다 — "완료" 또는 "이름 쓰고 건너뛰기"로만 나간다
    if (modalType === MODAL_TYPES.ONBOARDING) return;
    if (modalType === MODAL_TYPES.LOGIN) {
      closeModalWithRedirectClear();
    } else {
      closeModal();
    }
  };

  const renderModal = () => {
    switch (modalType) {
      case MODAL_TYPES.LOGIN:
        return <LoginModal onClose={closeModalWithRedirectClear} />;
      case MODAL_TYPES.SIGNUP:
        return <SignupModal onClose={closeModal} />;
      case MODAL_TYPES.ONBOARDING:
        return <OnboardingModal onClose={handleOnboardingClose} />;
      case MODAL_TYPES.FIND_EMAIL:
        return <FindEmailModal onClose={closeModal} />;
      case MODAL_TYPES.PASSWORD:
        return <PasswordModal onClose={closeModal} />;
      case MODAL_TYPES.RESET:
        return <ResetModal onClose={closeModal} />;
      default:
        return null;
    }
  };

  return ReactDOM.createPortal(
    <div
      className={
        modalType === MODAL_TYPES.ONBOARDING
          ? 'fixed inset-0 z-50 flex h-[100dvh] items-center justify-center bg-black/50 px-3 pb-20 pt-6 min-[800px]:py-6'
          : 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 pb-20 min-[800px]:pb-0 md:px-0'
      }
      onClick={handleBackdropClose}
    >
      <div onClick={(e) => e.stopPropagation()}>
        <Suspense fallback={null}>{renderModal()}</Suspense>
      </div>
    </div>,
    document.getElementById('root') as HTMLElement,
  );
};

export default Modals;
